import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import {
  getAbonnement,
  getAbonnementTimeline,
  transitionAbonnement,
} from "@/features/abonnementen/abonnementen.api";
import { Status } from "@/components/ui/Status";
import { useBreadcrumb } from "@/components/shell/BreadcrumbContext";
import { fmtMoney, fmtDate, fmtDayTime } from "@/lib/format";
import type { Abonnement, AbonnementStatus, TimelineEvent } from "@/lib/types";

export const Route = createFileRoute("/_shell/abonnementen/$id")({
  component: DossierPage,
});

const FACT_LABEL: Record<string, string> = { maand: "Per maand", jaar: "Per jaar" };
const BETAAL_LABEL: Record<string, string> = { incasso: "Automatische incasso", factuur: "Op factuur" };

type Tab = "overzicht" | "contract" | "tijdlijn" | "audit";

function DossierPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { setDetail } = useBreadcrumb();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("overzicht");

  const q = useQuery({ queryKey: ["abonnement", id], queryFn: () => getAbonnement(id) });
  const timelineQuery = useQuery({
    queryKey: ["abonnement", id, "timeline"],
    queryFn: () => getAbonnementTimeline(id),
  });
  const a = q.data;

  useEffect(() => {
    setDetail(a?.nummer ?? id);
    return () => setDetail(null);
  }, [a?.nummer, id, setDetail]);

  const transition = useMutation({
    mutationFn: (status: string) => transitionAbonnement(a!.id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["abonnement", id] });
      void queryClient.invalidateQueries({ queryKey: ["abonnement", id, "timeline"] });
      void queryClient.invalidateQueries({ queryKey: ["abonnementen"] });
    },
  });

  if (q.isLoading) {
    return <div className="app-page">{t("common.loading")}</div>;
  }
  if (!a) {
    return (
      <div className="app-page">
        <div className="panel">
          <div className="empty">
            <div className="empty__icon">
              <Icon name="alert-circle" size={22} />
            </div>
            <h3>{t("common.error")}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dossier-head">
        <div className="dossier-head__main">
          <div className="idrow">
            <span className="did">{a.nummer}</span>
            <button
              className="dtw-btn dtw-btn--ghost dtw-btn--icon dtw-btn--sm"
              onClick={() => void navigator.clipboard?.writeText(a.nummer)}
              aria-label={t("action.copy")}
              title={t("action.copy")}
            >
              <Icon name="copy" size={14} />
            </button>
            {/* Geen pakketchip meer: een abonnement heeft er meerdere. */}
            <span className="dtw-chip dtw-chip--indigo">
              {t("common.results", { count: a.onderdelenSamenvatting.aantal })}
            </span>
            {a.onderdelenSamenvatting.aantalWachtOpIngangsdatum > 0 && (
              <span className="dtw-chip" title={t("onderdelen.wachtOpDatumBody")}>
                <Icon name="clock" size={12} /> {t("onderdelen.wachtOpDatum")}
              </span>
            )}
          </div>
          <div className="metarow">
            <span>{a.klant.naam}</span>
            {a.klant.plaats && (
              <>
                <span className="sep">·</span> {a.klant.plaats}
              </>
            )}
            <span className="sep">·</span> {t("abonnement.field.maanden", { count: a.looptijdMaanden })}
            {a.verlengtOp && (
              <>
                <span className="sep">·</span> {t("abonnement.field.verlengtOp")} {fmtDate(a.verlengtOp)}
              </>
            )}
          </div>
        </div>
        <div className="dossier-head__aside">
          <Status status={a.status} />
          <TransitionMenu
            allowed={a.allowedTransitions}
            pending={transition.isPending}
            onPick={(s) => transition.mutate(s)}
          />
        </div>
      </div>

      <div className="app-page" style={{ paddingTop: "var(--space-5)" }}>
        <div className="dtw-tabs" style={{ marginBottom: "var(--space-5)" }}>
          {(["overzicht", "contract", "tijdlijn", "audit"] as Tab[]).map((tk) => (
            <button
              key={tk}
              className={`dtw-tab${tab === tk ? " is-active" : ""}`}
              onClick={() => setTab(tk)}
            >
              {t(`abonnement.tab.${tk}` as never)}
            </button>
          ))}
        </div>

        {tab === "overzicht" && (
          <div className="grid">
            <div className="dtw-vstack" style={{ gap: "var(--space-4)" }}>
              <SummaryCard a={a} />
              <OnderdelenCard a={a} />
              <ContractCard a={a} />
            </div>
            <div className="dtw-vstack" style={{ gap: "var(--space-4)" }}>
              <TimelineCard events={timelineQuery.data ?? []} />
              <PartiesCard a={a} />
              <AuditCard events={timelineQuery.data ?? []} />
            </div>
          </div>
        )}
        {tab === "contract" && <ContractCard a={a} />}
        {tab === "tijdlijn" && <TimelineCard events={timelineQuery.data ?? []} />}
        {tab === "audit" && <AuditCard events={timelineQuery.data ?? []} full />}
      </div>
    </>
  );
}

function TransitionMenu({
  allowed,
  pending,
  onPick,
}: {
  allowed: AbonnementStatus[];
  pending: boolean;
  onPick: (s: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div className="dossier-actions" ref={ref}>
      <button
        className="dtw-btn dtw-btn--primary"
        disabled={allowed.length === 0 || pending}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="refresh-cw" size={14} /> {t("abonnement.changeStatus")}
      </button>
      {open && allowed.length > 0 && (
        <div
          className="dtw-popover"
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + var(--space-1_5))",
            minWidth: "calc(var(--space-24) * 1.8)",
          }}
        >
          <div className="dtw-menu">
            <div className="dtw-menu__header">{t("abonnement.moveTo")}</div>
            {allowed.map((s) => (
              <button
                key={s}
                className={`dtw-menu__item${s === "opgezegd" || s === "verlopen" ? " dtw-menu__item--danger" : ""}`}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onPick(s);
                }}
              >
                <Icon name={s === "opgezegd" || s === "verlopen" ? "ban" : "arrow-right"} size={15} />
                <span>{t(`status.${s}` as never)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="fld">
      <div className="l">{label}</div>
      <div className={`v${mono ? " mono" : ""}`}>{value}</div>
    </div>
  );
}

function SummaryCard({ a }: { a: Abonnement }) {
  const { t } = useTranslation();
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("abonnement.section.samenvatting")}</span>
      </div>
      <div className="dtw-card__body">
        <div className="fields">
          <Field label={t("abonnement.field.klant")} value={a.klant.naam} />
          <Field
            label={t("abonnement.field.pakket")}
            value={t("common.results", { count: a.onderdelenSamenvatting.aantal })}
          />
          <Field label={t("abonnement.field.maandbedrag")} value={fmtMoney(a.maandbedragCents)} mono />
          {/* Alles wordt per jaar gefactureerd: 12 × het maandbedrag. */}
          <Field label={t("abonnement.field.jaarbedrag")} value={fmtMoney(a.jaarbedragCents)} mono />
          <Field label={t("abonnement.field.opzegbaarTot")} value={fmtDate(a.opzegbaarTot)} mono />
          <Field label={t("abonnement.field.startdatum")} value={fmtDate(a.startdatum)} mono />
          <Field label={t("abonnement.field.verlengtOp")} value={fmtDate(a.verlengtOp)} mono />
          <Field
            label={t("abonnement.field.looptijd")}
            value={t("abonnement.field.maanden", { count: a.looptijdMaanden })}
          />
          <Field
            label={t("abonnement.field.opzegtermijn")}
            value={t("abonnement.field.maanden", { count: a.opzegtermijnMaanden })}
          />
          <Field label={t("abonnement.field.facturatie")} value={FACT_LABEL[a.facturatie] ?? a.facturatie} />
          <Field label={t("abonnement.field.betaalwijze")} value={BETAAL_LABEL[a.betaalwijze] ?? a.betaalwijze} />
          {a.contactNaam && <Field label={t("klanten.col.contact")} value={a.contactNaam} />}
        </div>
      </div>
    </div>
  );
}

// De onderdelen van het abonnement — wat de klant "de pakketten" noemt. Elk met
// een eigen status, ingangsdatum en overeengekomen bedrag. De afwijking t.o.v. de
// standaardprijs is afgeleid: er is één prijslijst en een contract kan achterlopen.
function OnderdelenCard({ a }: { a: Abonnement }) {
  const { t } = useTranslation();
  const onderdelen = a.onderdelen;

  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("abonnement.section.pakket")}</span>
        <span className="dtw-chip">{t("common.results", { count: onderdelen.length })}</span>
      </div>
      <div className="dtw-card__body flush">
        {onderdelen.length === 0 ? (
          <div className="empty">
            <h3>{t("onderdelen.empty.title")}</h3>
            <p>{t("onderdelen.empty.body")}</p>
          </div>
        ) : (
          <div className="dtw-table-wrap">
            <table className="dtw-table onderdelen-table">
              <thead>
                <tr>
                  <th>{t("onderdelen.col.productcode")}</th>
                  <th>{t("onderdelen.col.onderdeel")}</th>
                  <th>{t("onderdelen.col.status")}</th>
                  <th className="num">{t("onderdelen.col.bedrag")}</th>
                  <th className="num">{t("onderdelen.col.afwijking")}</th>
                  <th>{t("onderdelen.col.ingangsdatum")}</th>
                  <th className="num">{t("onderdelen.col.eerstePeriode")}</th>
                </tr>
              </thead>
              <tbody>
                {onderdelen.map((o) => (
                  <tr key={o.id} className={o.status === "vervallen" ? "is-muted" : ""}>
                    <td className="mono">{o.pakket.productcode}</td>
                    <td>
                      <div className="dtw-table__cell-stack">
                        <span className="primary">
                          {o.pakket.productgroepLabel} {o.pakket.model}
                        </span>
                        <span className="secondary t-tertiary">
                          {o.pakket.niveauLabel}
                          {o.vervangenDoorId ? ` · ${t("onderdelen.opgevolgdDoor")}` : ""}
                          {o.vervangtOnderdeelId ? ` · ${t("onderdelen.vervangt")}` : ""}
                        </span>
                      </div>
                    </td>
                    <td>
                      <Status status={o.status} />
                    </td>
                    <td className="num mono">{fmtMoney(o.overeengekomenMaandbedragCents)}</td>
                    <td className="num mono">
                      {o.afwijking.verschilCents === 0 ? (
                        <span className="t-tertiary">{t("onderdelen.opStandaard")}</span>
                      ) : (
                        <span className={o.afwijking.onderStandaard ? "t-warning" : "t-danger"}>
                          {fmtPercent(o.afwijking.percentageBasispunten)}
                        </span>
                      )}
                    </td>
                    <td className="mono">
                      {o.ingangsdatum ? (
                        fmtDate(o.ingangsdatum)
                      ) : (
                        <span className="t-tertiary">{t("onderdelen.geenDatum")}</span>
                      )}
                    </td>
                    <td className="num mono">
                      {o.eerstePeriode ? (
                        <span
                          title={
                            o.eerstePeriode.volledigePeriode
                              ? t("onderdelen.volledigePeriode")
                              : t("onderdelen.proRataNote", {
                                  dagen: o.eerstePeriode.dagen,
                                  totaal: o.eerstePeriode.dagenInPeriode,
                                })
                          }
                        >
                          {fmtMoney(o.eerstePeriode.bedragCents)}
                        </span>
                      ) : (
                        <span className="t-tertiary">{t("common.none")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>{t("onderdelen.totaal")}</td>
                  <td className="num mono">{fmtMoney(a.maandbedragCents)}</td>
                  <td colSpan={2} />
                  <td className="num mono">{fmtMoney(a.jaarbedragCents)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** Basispunten → een leesbaar percentage in NL-notatie: -2410 → "−24,1%". */
function fmtPercent(basispunten: number): string {
  const pct = basispunten / 100;
  const tekst = Math.abs(pct).toLocaleString("nl-NL", { maximumFractionDigits: 1 });
  return `${pct < 0 ? "−" : "+"}${tekst}%`;
}

function ContractCard({ a }: { a: Abonnement }) {
  const { t } = useTranslation();
  if (!a.contract) {
    return (
      <div className="dtw-card">
        <div className="dtw-card__header">
          <span className="dtw-card__title">{t("abonnement.section.contract")}</span>
        </div>
        <div className="dtw-card__body">
          <p className="t-tertiary" style={{ fontSize: "var(--fs-sm)" }}>
            Nog geen contract gekoppeld.
          </p>
        </div>
      </div>
    );
  }
  const c = a.contract;
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("abonnement.section.contract")}</span>
      </div>
      <div className="dtw-card__body dtw-vstack" style={{ gap: "var(--space-4)" }}>
        <div className="fields">
          <Field label="Contractnummer" value={c.nummer} mono />
          <Field label="Ingangsdatum" value={fmtDate(c.ingangsdatum)} mono />
          <Field
            label="Automatische verlenging"
            value={c.automatischeVerlenging ? `Ja · ${c.verlengingsduurMaanden} maanden` : "Nee"}
          />
          <Field
            label="Opzegtermijn"
            value={t("abonnement.field.maanden", { count: c.opzegtermijnMaanden })}
          />
        </div>
        {c.automatischeVerlenging && c.opzegbaarTot && (
          <div className="dtw-alert dtw-alert--info">
            <Icon name="info" size={16} />
            <div>
              Automatische verlenging staat aan. Opzeggen kan tot en met <strong>{fmtDate(c.opzegbaarTot)}</strong>.
            </div>
          </div>
        )}
        <div className="dtw-hstack">
          <button className="dtw-btn dtw-btn--secondary dtw-btn--sm" disabled={!c.pdfUrl}>
            <Icon name="download" size={13} /> Download contract (PDF)
          </button>
        </div>
      </div>
    </div>
  );
}

function auditTitle(e: TimelineEvent, statusLabel: (w: string) => string): string {
  const p = e.payload as { to?: string };
  switch (e.eventType) {
    case "abonnement.created":
      return "Aangemaakt";
    case "abonnement.status_changed":
      return `Status → ${p.to ? statusLabel(p.to) : "?"}`;
    case "abonnement.updated":
      return "Bijgewerkt";
    case "contract.created":
      return "Contract aangemaakt";
    default:
      return e.eventType;
  }
}

function TimelineCard({ events }: { events: TimelineEvent[] }) {
  const { t } = useTranslation();
  const statusLabel = (w: string) => t(`status.${w}` as never);
  const chrono = [...events].reverse(); // oldest → newest
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("abonnement.section.tijdlijn")}</span>
      </div>
      <div className="dtw-card__body">
        <div className="timeline">
          {chrono.map((e, i) => (
            <div className={`tl-step${i === chrono.length - 1 ? " current" : ""}`} key={e.id}>
              <div className="tl-dot">
                <Icon name={i === chrono.length - 1 ? "refresh-cw" : "check"} size={11} />
              </div>
              <div className="tl-body">
                <div className="tl-time">{fmtDayTime(e.at)}</div>
                <div className="tl-title">{auditTitle(e, statusLabel)}</div>
                {typeof (e.payload as { actorName?: string }).actorName === "string" && (
                  <div className="tl-meta">Door {(e.payload as { actorName?: string }).actorName}</div>
                )}
              </div>
            </div>
          ))}
          {chrono.length === 0 && (
            <p className="t-tertiary" style={{ fontSize: "var(--fs-sm)" }}>
              {t("common.none")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function PartiesCard({ a }: { a: Abonnement }) {
  const { t } = useTranslation();
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("abonnement.section.partijen")}</span>
      </div>
      <div className="dtw-card__body">
        <div className="party">
          <div className="role">Klant</div>
          <div className="nm">{a.klant.naam}</div>
          {a.klant.plaats && <div className="ln">{a.klant.plaats}</div>}
        </div>
        {a.contactNaam && (
          <div className="party">
            <div className="role">Contactpersoon</div>
            <div className="nm">{a.contactNaam}</div>
            {a.facturatieEmail && (
              <div className="ln">
                <Icon name="mail" size={13} /> {a.facturatieEmail}
              </div>
            )}
          </div>
        )}
        <div className="party">
          <div className="role">Accountbeheer</div>
          <div className="nm">Datow Coöperatie U.A.</div>
        </div>
      </div>
    </div>
  );
}

function AuditCard({ events, full }: { events: TimelineEvent[]; full?: boolean }) {
  const { t } = useTranslation();
  const statusLabel = (w: string) => t(`status.${w}` as never);
  const shown = full ? events : events.slice(0, 3);
  return (
    <div className="dtw-card">
      <div className="dtw-card__header">
        <span className="dtw-card__title">{t("abonnement.section.audit")}</span>
      </div>
      <div className="dtw-card__body">
        <div className="audit">
          {shown.map((e) => (
            <div className="audit-row" key={e.id}>
              <span className="t">{fmtDayTime(e.at)}</span>
              <span>
                <span className="who">
                  {(e.payload as { actorName?: string }).actorName ?? "Systeem"}
                </span>{" "}
                <span className="act">— {auditTitle(e, statusLabel)}</span>
              </span>
            </div>
          ))}
          {shown.length === 0 && (
            <p className="t-tertiary" style={{ fontSize: "var(--fs-sm)" }}>
              {t("common.none")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
